import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTranslation } from "react-i18next";
import { getAllMuestras } from "../../api/Muestras/muestraApi";
import { getProducts } from "../../api/Design/productApi";

function muestraLabel(m) {
  if (!m) return "";
  const code = String(m.MUESTRA || m.muestra || "").trim();
  const id = m.ID_MUESTRA ?? m.id;
  const product = String(
    m.producto?.nombre ||
      m.Producto?.nombre ||
      m.productName ||
      m.NOMBRE_PRODUCTO ||
      "",
  ).trim();
  if (code && product) return `${code} · ${product}`;
  if (code) return code;
  return id != null ? `#${id}` : "";
}

function productLabel(p) {
  if (!p) return "";
  const name = String(p.nombre || p.Nombre || "").trim();
  const id = p.id ?? p.ID_PRODUCTO;
  return name || (id != null ? `#${id}` : "");
}

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 10% { opacity: 0.25; transform: translateY(6px) scale(0.9); }
  30%, 65% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.35; transform: translateY(3px) scale(0.95); }
`;

const flyLink = keyframes`
  0%, 12% { opacity: 0.2; transform: translateX(-8px) scale(0.9); }
  40%, 70% { opacity: 1; transform: translateX(0) scale(1); }
  100% { opacity: 0.35; transform: translateX(6px) scale(0.95); }
`;

const pulseArrow = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.45; }
  50% { transform: translateX(4px); opacity: 1; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: { xs: 72, sm: 68 },
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

/** Media → muestra existente (chip) */
function MuestraPreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.14)}>
      {[0, 1].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 10 + i * 14,
            top: 14 + i * 6,
            width: 30,
            height: 38,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.4),
            boxShadow: `0 4px 10px ${alpha(accent, 0.16)}`,
            animation: `${flyLink} 3.6s ease-in-out infinite`,
            animationDelay: `${i * 0.14}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: "46%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: accent,
          animation: `${pulseArrow} 1.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 12,
          top: 14,
          width: 40,
          height: 40,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.22)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <StyleOutlinedIcon sx={{ fontSize: 20, color: accent }} />
      </Box>
    </Stage>
  );
}

/** Media → diseño → nueva muestra */
function ProductPreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.14)}>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          top: 14,
          width: 38,
          height: 38,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${pop} 3.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DashboardCustomizeOutlinedIcon sx={{ fontSize: 20, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "46%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: accent,
          animation: `${pulseArrow} 1.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 12,
          top: 14,
          px: 0.85,
          py: 0.55,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.18),
          border: "1px solid",
          borderColor: alpha(accent, 0.5),
          animation: `${bob} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography
          sx={{ fontSize: "0.58rem", fontWeight: 900, color: accent }}
        >
          + MUESTRA
        </Typography>
      </Box>
    </Stage>
  );
}

function IntentCard({
  active,
  preview: Preview,
  title,
  body,
  accent,
  onClick,
  delayMs = 0,
  disabled = false,
}) {
  const theme = useTheme();
  const muted = theme.palette.grey[400];
  const ink = active ? accent : muted;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      sx={{
        p: 0,
        m: 0,
        width: "100%",
        minWidth: 0,
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: "2.5px solid",
        borderColor: active ? accent : alpha(theme.palette.text.primary, 0.12),
        bgcolor: active
          ? alpha(accent, 0.12)
          : alpha(theme.palette.grey[500], 0.06),
        boxShadow: active ? `0 10px 24px ${alpha(accent, 0.28)}` : "none",
        opacity: active ? 1 : 0.55,
        filter: active ? "none" : "grayscale(0.85)",
        transition:
          "border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, opacity 0.18s ease, filter 0.18s ease",
        font: "inherit",
        color: "inherit",
        animation: `${rise} 320ms ease both`,
        animationDelay: `${delayMs}ms`,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        ...motionSafe,
        "&:active": disabled ? undefined : { transform: "scale(0.985)" },
        "&:hover": disabled
          ? undefined
          : {
              opacity: 1,
              filter: "none",
              borderColor: active ? accent : alpha(accent, 0.55),
              bgcolor: alpha(accent, active ? 0.16 : 0.08),
            },
        "&:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Preview accent={ink} />
      <Box
        sx={{
          px: 1.15,
          py: 1.1,
          bgcolor: active
            ? alpha(accent, 0.1)
            : alpha(theme.palette.grey[500], 0.04),
          borderTop: "1px solid",
          borderColor: active
            ? alpha(accent, 0.22)
            : alpha(theme.palette.text.primary, 0.08),
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {active ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 18, color: accent }} />
          ) : null}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              fontSize: { xs: "0.8rem", sm: "0.78rem" },
              color: ink,
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.35,
            lineHeight: 1.35,
            fontSize: { xs: "0.7rem", sm: "0.68rem" },
            color: active
              ? alpha(theme.palette.text.primary, 0.65)
              : alpha(theme.palette.text.primary, 0.4),
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Adjuntar media de reunión a:
 * - muestra existente (chip + copiar archivos), o
 * - diseño existente (abrir creación de muestra sobre ese producto).
 */
export default function MeetingLinkMuestraDialog({
  open,
  onClose,
  onConfirmMuestra,
  onConfirmProduct,
  mediaCount = 0,
  linking = false,
  fullScreen = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [muestras, setMuestras] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedMuestra, setSelectedMuestra] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [muestraInput, setMuestraInput] = useState("");
  const [productInput, setProductInput] = useState("");
  const [loadError, setLoadError] = useState("");

  const muestraAccent = theme.palette.primary.main || "#0f766e";
  const productAccent = "#9a3412";

  useEffect(() => {
    if (!open) {
      setTab(0);
      setSelectedMuestra(null);
      setSelectedProduct(null);
      setMuestraInput("");
      setProductInput("");
      setLoadError("");
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getAllMuestras(), getProducts()])
      .then(([mData, pData]) => {
        if (cancelled) return;
        setMuestras(Array.isArray(mData) ? mData : []);
        setProducts(Array.isArray(pData) ? pData : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || t("Meetings.linkMuestraLoadError"));
        setMuestras([]);
        setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const filteredMuestras = useMemo(() => {
    const q = String(muestraInput || "")
      .trim()
      .toLowerCase();
    const list = muestras || [];
    if (!q) return list.slice(0, 40);
    return list
      .filter((m) => {
        const hay = `${muestraLabel(m)} ${m.ID_MUESTRA || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [muestras, muestraInput]);

  const filteredProducts = useMemo(() => {
    const q = String(productInput || "")
      .trim()
      .toLowerCase();
    const list = products || [];
    if (!q) return list.slice(0, 40);
    return list
      .filter((p) => {
        const hay = `${productLabel(p)} ${p.id || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 60);
  }, [products, productInput]);

  const canConfirmMuestra =
    tab === 0 &&
    !linking &&
    !loading &&
    selectedMuestra &&
    Number(selectedMuestra.ID_MUESTRA || selectedMuestra.id) > 0;

  const canConfirmProduct =
    tab === 1 &&
    !linking &&
    !loading &&
    selectedProduct &&
    Number(selectedProduct.id || selectedProduct.ID_PRODUCTO) > 0;

  const handleConfirm = () => {
    if (tab === 0) onConfirmMuestra?.(selectedMuestra);
    else onConfirmProduct?.(selectedProduct);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: (th) => alpha(th.palette.background.default, 0.55),
    },
  };

  const accent = tab === 0 ? muestraAccent : productAccent;

  return (
    <Dialog
      open={open}
      onClose={() => !linking && onClose?.()}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: fullScreen ? "100%" : "min(92vh, 820px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              "#9a3412",
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <LinkOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.linkExistingTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
              >
                {t("Meetings.linkExistingBody", { count: mediaCount })}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={linking}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        {mediaCount > 0 ? (
          <Chip
            size="small"
            icon={
              <CollectionsOutlinedIcon sx={{ fontSize: "16px !important" }} />
            }
            label={t("Meetings.createSampleMediaChip", { count: mediaCount })}
            sx={{
              mt: 1.25,
              height: 26,
              fontWeight: 800,
              borderRadius: 1.25,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "& .MuiChip-icon": { color: "primary.main" },
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.15,
            mb: 2,
          }}
        >
          <IntentCard
            active={tab === 0}
            preview={MuestraPreview}
            title={t("Meetings.linkTabMuestra")}
            body={t("Meetings.linkTabMuestraHint")}
            accent={muestraAccent}
            onClick={() => !linking && setTab(0)}
            delayMs={40}
            disabled={linking}
          />
          <IntentCard
            active={tab === 1}
            preview={ProductPreview}
            title={t("Meetings.linkTabProduct")}
            body={t("Meetings.linkTabProductHint")}
            accent={productAccent}
            onClick={() => !linking && setTab(1)}
            delayMs={90}
            disabled={linking}
          />
        </Box>

        {loadError ? (
          <Typography
            color="error"
            variant="body2"
            fontWeight={700}
            sx={{ mb: 1.5 }}
          >
            {loadError}
          </Typography>
        ) : null}

        <Box
          key={`link-tab-${tab}`}
          sx={{
            animation: `${rise} 280ms ease both`,
            ...motionSafe,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mb: 1.25,
              lineHeight: 1.4,
              fontWeight: 600,
            }}
          >
            {tab === 0
              ? t("Meetings.linkMuestraBody", { count: mediaCount })
              : t("Meetings.linkProductBody", { count: mediaCount })}
          </Typography>
          {tab === 0 ? (
            <Autocomplete
              options={filteredMuestras}
              loading={loading}
              value={selectedMuestra}
              onChange={(_, value) => setSelectedMuestra(value)}
              inputValue={muestraInput}
              onInputChange={(_, value) => setMuestraInput(value)}
              getOptionLabel={(opt) => muestraLabel(opt)}
              isOptionEqualToValue={(a, b) =>
                Number(a?.ID_MUESTRA ?? a?.id) ===
                Number(b?.ID_MUESTRA ?? b?.id)
              }
              filterOptions={(x) => x}
              disabled={linking}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label={t("Meetings.linkMuestraSearch")}
                  placeholder={t("Meetings.linkMuestraPlaceholder")}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <Autocomplete
              options={filteredProducts}
              loading={loading}
              value={selectedProduct}
              onChange={(_, value) => setSelectedProduct(value)}
              inputValue={productInput}
              onInputChange={(_, value) => setProductInput(value)}
              getOptionLabel={(opt) => productLabel(opt)}
              isOptionEqualToValue={(a, b) =>
                Number(a?.id ?? a?.ID_PRODUCTO) ===
                Number(b?.id ?? b?.ID_PRODUCTO)
              }
              filterOptions={(x) => x}
              disabled={linking}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label={t("Meetings.linkProductSearch")}
                  placeholder={t("Meetings.linkProductPlaceholder")}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button onClick={onClose} disabled={linking} sx={{ fontWeight: 700 }}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!(canConfirmMuestra || canConfirmProduct)}
          onClick={handleConfirm}
          startIcon={
            linking ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            fontWeight: 800,
            borderRadius: 2,
            px: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
            bgcolor: accent,
            "&:hover": {
              bgcolor: accent,
              filter: "brightness(0.94)",
            },
          }}
        >
          {tab === 0
            ? t("Meetings.linkMuestraConfirm")
            : t("Meetings.linkProductConfirm")}
        </Button>
      </Stack>
    </Dialog>
  );
}
